// @ts-check

/**
 * Burst configuration of the native Cloudflare rate limiting bindings.
 *
 * These values MUST match the `ratelimits` entries in `server/wrangler.jsonc`.
 * They live here rather than in the Worker because two very different callers
 * need them: the Worker, to report a truthful `Retry-After`, and the admin
 * dashboard, to warn when an operator raises a tier budget above the ceiling
 * that will actually apply. Cloudflare only accepts a period of 10 or 60.
 *
 * @type {Record<string, { limit: number, periodSeconds: number }>}
 */
export const RATE_LIMIT_TIER_BURSTS = {
  strict: { limit: 30, periodSeconds: 60 },
  standard: { limit: 300, periodSeconds: 60 },
  media: { limit: 600, periodSeconds: 60 }
};

/** Window that operator-facing tier budgets are expressed in, in minutes. */
export const RATE_LIMIT_BUDGET_WINDOW_MINUTES = 10;

/**
 * Largest budget a tier can actually deliver over the operator-facing window,
 * given its burst ceiling.
 *
 * A budget above this is not rejected, but it is unreachable: the binding denies
 * the request before the long window is ever exhausted, so the extra headroom
 * never materialises. Raising it for real means editing `wrangler.jsonc` and
 * deploying.
 *
 * @param {string} tier
 * @returns {number} 0 when the tier has no configured binding
 */
export function tierBurstBudgetEquivalent(tier) {
  const burst = RATE_LIMIT_TIER_BURSTS[tier];
  if (!burst || !burst.periodSeconds || !burst.limit) return 0;
  const periodsPerWindow = (RATE_LIMIT_BUDGET_WINDOW_MINUTES * 60) / burst.periodSeconds;
  return Math.round(burst.limit * periodsPerWindow);
}
